import { useState, useEffect, useCallback } from 'react'
import {
  Download, RefreshCw, CheckCircle, XCircle, Clock, Play, Plus,
  Trash2, Settings, AlertCircle, RotateCcw, ChevronDown, ChevronUp,
  FileSpreadsheet, FileText, Database, Zap, Globe, Package
} from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import api from '../lib/api'

// ── Types ──────────────────────────────────────────────────────────
interface Integration {
  id: string; name: string; type: string; exportFormat: string
  isActive: boolean; lastRunAt: string | null; lastRunStatus: string | null
  config?: string; rubriquesMap?: string; createdAt: string
}
interface Run {
  id: string; integrationId: string; status: string; period: string | null
  recordCount: number; errorCount: number; fileName: string | null
  errorDetails: string | null; startedAt: string; finishedAt: string | null
  triggeredBy: string | null
  integration?: { id: string; name: string; type: string; exportFormat: string }
}

// ── Constants ─────────────────────────────────────────────────────
const INTEGRATION_TYPES = [
  { value: 'payfit',  label: 'Payfit',       icon: <Zap size={14} className="text-orange-500" /> },
  { value: 'silae',   label: 'Silae',         icon: <FileSpreadsheet size={14} className="text-purple-500" /> },
  { value: 'sage',    label: 'Sage Paie',     icon: <FileSpreadsheet size={14} className="text-blue-500" /> },
  { value: 'adp',     label: 'ADP',           icon: <Globe size={14} className="text-red-500" /> },
  { value: 'cegid',   label: 'Cegid',         icon: <Database size={14} className="text-indigo-500" /> },
  { value: 'lucca',   label: 'Lucca',         icon: <Package size={14} className="text-green-500" /> },
  { value: 'dsn',     label: 'DSN',           icon: <FileText size={14} className="text-amber-500" /> },
  { value: 'csv',     label: 'Export CSV',    icon: <FileText size={14} className="text-gray-500" /> },
]
const TYPE_FORMATS: Record<string, string[]> = {
  payfit: ['payfit'], silae: ['silae'], sage: ['sage'],
  adp: ['adp'], cegid: ['cegid'], lucca: ['csv'], dsn: ['dsn'], csv: ['csv']
}
const STATUS_CFG: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  success: { label: 'Succès',   cls: 'text-green-600 bg-green-50 border-green-200',  icon: <CheckCircle size={13} /> },
  error:   { label: 'Erreur',   cls: 'text-red-600 bg-red-50 border-red-200',        icon: <XCircle size={13} /> },
  partial: { label: 'Partiel',  cls: 'text-amber-600 bg-amber-50 border-amber-200',  icon: <AlertCircle size={13} /> },
  running: { label: 'En cours', cls: 'text-blue-600 bg-blue-50 border-blue-200',     icon: <Clock size={13} className="animate-spin" /> },
}

function typeIcon(type: string) {
  return INTEGRATION_TYPES.find(t => t.value === type)?.icon ?? <FileText size={14} className="text-gray-400" />
}

const CURRENT_MONTH = new Date().toISOString().slice(0, 7)

// ── Component ─────────────────────────────────────────────────────
export default function IntegrationLogs() {
  const [tab, setTab]                   = useState<'config'|'journal'>('journal')
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [runs, setRuns]                 = useState<Run[]>([])
  const [loading, setLoading]           = useState(true)
  const [runLoading, setRunLoading]     = useState<string | null>(null)

  // Filters
  const [filterType,   setFilterType]   = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  // Modals
  const [showAdd,  setShowAdd]  = useState(false)
  const [editInteg, setEditInteg] = useState<Integration | null>(null)
  const [runDetail, setRunDetail] = useState<Run | null>(null)

  // Run form
  const [runPeriod, setRunPeriod] = useState(CURRENT_MONTH)

  // Add/edit form
  const [form, setForm] = useState({ name: '', type: 'silae', exportFormat: 'silae', isActive: true })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [ir, rr] = await Promise.all([
        api.get('/integrations'),
        api.get('/integrations/runs')
      ])
      setIntegrations(ir.data)
      setRuns(rr.data)
    } catch { /* silently fail */ }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // ── CRUD integrations ────────────────────────────────────────────
  async function saveInteg() {
    if (!form.name || !form.type) return
    if (editInteg) {
      await api.put(`/integrations/${editInteg.id}`, form)
    } else {
      await api.post('/integrations', form)
    }
    setShowAdd(false); setEditInteg(null)
    setForm({ name: '', type: 'silae', exportFormat: 'silae', isActive: true })
    load()
  }

  async function deleteInteg(id: string) {
    if (!confirm('Supprimer cette intégration et tout son historique ?')) return
    await api.delete(`/integrations/${id}`)
    load()
  }

  async function toggleActive(integ: Integration) {
    await api.put(`/integrations/${integ.id}`, { isActive: !integ.isActive })
    load()
  }

  // ── Trigger run ──────────────────────────────────────────────────
  async function triggerRun(integ: Integration) {
    if (!runPeriod) return
    setRunLoading(integ.id)
    try {
      await api.post(`/integrations/${integ.id}/run`, { period: runPeriod })
      load()
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erreur lors du run')
    }
    setRunLoading(null)
  }

  // ── Replay ────────────────────────────────────────────────────────
  async function replay(run: Run) {
    setRunLoading(run.id)
    try {
      await api.post(`/integrations/runs/${run.id}/replay`)
      load()
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erreur replay')
    }
    setRunLoading(null)
  }

  // ── Download ─────────────────────────────────────────────────────
  async function downloadRun(run: Run) {
    const res = await api.get(`/integrations/runs/${run.id}/download`, { responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a'); a.href = url
    a.download = run.fileName || `export_${run.period}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  const filteredRuns = runs.filter(r => {
    if (filterType && r.integration?.type !== filterType) return false
    if (filterStatus && r.status !== filterStatus) return false
    return true
  })

  const successCount = runs.filter(r => r.status === 'success').length
  const errorCount   = runs.filter(r => r.status === 'error').length

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 max-w-6xl">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Intégrations paie</h1>
          <p className="text-sm text-gray-500 mt-0.5">Connecteurs, exports et journal des synchronisations</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 bg-white border border-gray-200 px-3 py-2 rounded-lg shadow-sm transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
          <button onClick={() => { setEditInteg(null); setForm({ name: '', type: 'silae', exportFormat: 'silae', isActive: true }); setShowAdd(true) }}
            className="flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg shadow-sm transition-colors">
            <Plus size={14} /> Nouvelle intégration
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Intégrations', value: integrations.length, color: 'text-gray-900', bg: 'bg-gray-50' },
          { label: 'Actives',      value: integrations.filter(i => i.isActive).length, color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Runs réussis', value: successCount, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Erreurs',      value: errorCount,   color: 'text-red-700',   bg: 'bg-red-50' },
        ].map((k, i) => (
          <div key={i} className={`${k.bg} rounded-xl p-4 border border-gray-100`}>
            <p className="text-xs text-gray-500 font-medium">{k.label}</p>
            <p className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {([['journal','Journal des runs'],['config','Configurations']] as const).map(([t, lbl]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {/* ── Tab: Journal ───────────────────────────────────────────── */}
      {tab === 'journal' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3">
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Tous les types</option>
              {INTEGRATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Tous les statuts</option>
              <option value="success">Succès</option>
              <option value="error">Erreur</option>
              <option value="partial">Partiel</option>
              <option value="running">En cours</option>
            </select>
            <span className="text-sm text-gray-400 ml-auto">{filteredRuns.length} résultat{filteredRuns.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Intégration','Période','Fichier','Lignes','Statut','Démarré','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Chargement…</td></tr>
                )}
                {!loading && filteredRuns.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">Aucun run trouvé</td></tr>
                )}
                {filteredRuns.map(run => {
                  const sc = STATUS_CFG[run.status] || STATUS_CFG.error
                  let errors: string[] = []
                  try { errors = JSON.parse(run.errorDetails||'[]') } catch {}
                  return (
                    <tr key={run.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {typeIcon(run.integration?.type||'')}
                          <span className="font-medium text-gray-800">{run.integration?.name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 font-mono text-xs">{run.period||'—'}</td>
                      <td className="px-4 py-3">
                        {run.fileName
                          ? <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded truncate max-w-[160px] block">{run.fileName}</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{run.recordCount > 0 ? run.recordCount : '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${sc.cls}`}>
                          {sc.icon} {sc.label}
                        </span>
                        {errors.length > 0 && (
                          <p className="text-xs text-red-500 mt-1 max-w-[200px] truncate" title={errors[0]}>{errors[0]}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {format(new Date(run.startedAt), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {run.status === 'success' && run.fileContent && (
                            <button onClick={() => downloadRun(run)} title="Télécharger"
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors">
                              <Download size={14} />
                            </button>
                          )}
                          <button onClick={() => setRunDetail(run)} title="Détails"
                            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors">
                            <Settings size={14} />
                          </button>
                          <button onClick={() => replay(run)} disabled={runLoading === run.id} title="Rejouer"
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors disabled:opacity-40">
                            {runLoading === run.id ? <Clock size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Configurations ────────────────────────────────────── */}
      {tab === 'config' && (
        <div className="space-y-4">
          {/* Period picker for runs */}
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <Zap size={15} className="text-blue-500 flex-shrink-0" />
            <span className="text-sm text-blue-700 font-medium">Période de run :</span>
            <input type="month" value={runPeriod} onChange={e => setRunPeriod(e.target.value)}
              className="text-sm border border-blue-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <span className="text-xs text-blue-500">Sélectionnez la période avant de lancer un export</span>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">Chargement…</div>
          ) : integrations.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
              <Database size={32} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">Aucune intégration configurée</p>
              <p className="text-gray-400 text-sm mt-1">Créez votre première connexion avec un logiciel de paie</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {integrations.map(integ => {
                const typeInfo = INTEGRATION_TYPES.find(t => t.value === integ.type)
                const lastRun = runs.find(r => r.integrationId === integ.id)
                const sc = integ.lastRunStatus ? STATUS_CFG[integ.lastRunStatus] : null
                return (
                  <div key={integ.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
                        {typeIcon(integ.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">{integ.name}</h3>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {typeInfo?.label || integ.type} · {integ.exportFormat.toUpperCase()}
                          </span>
                          {sc && (
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${sc.cls}`}>
                              {sc.icon} {sc.label}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${integ.isActive ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                            {integ.isActive ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                        {integ.lastRunAt && (
                          <p className="text-xs text-gray-400 mt-1">
                            Dernier run : {format(new Date(integ.lastRunAt), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                            {lastRun?.recordCount ? ` · ${lastRun.recordCount} enregistrements` : ''}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => triggerRun(integ)} disabled={runLoading === integ.id || !integ.isActive}
                          className="flex items-center gap-1.5 text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400 text-white px-3 py-1.5 rounded-lg transition-colors">
                          {runLoading === integ.id ? <Clock size={12} className="animate-spin" /> : <Play size={12} />}
                          Lancer
                        </button>
                        <button onClick={() => toggleActive(integ)}
                          className={`text-xs px-2.5 py-1.5 rounded-lg transition-colors border ${integ.isActive ? 'text-amber-600 border-amber-200 hover:bg-amber-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}>
                          {integ.isActive ? 'Désactiver' : 'Activer'}
                        </button>
                        <button onClick={() => { setEditInteg(integ); setForm({ name: integ.name, type: integ.type, exportFormat: integ.exportFormat, isActive: integ.isActive }); setShowAdd(true) }}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
                          <Settings size={14} />
                        </button>
                        <button onClick={() => deleteInteg(integ.id)}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Modal: Add/Edit integration ──────────────────────────────── */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) { setShowAdd(false); setEditInteg(null) } }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5">
            <h2 className="text-lg font-bold text-gray-900">{editInteg ? 'Modifier l\'intégration' : 'Nouvelle intégration'}</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ex : Silae Production"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value, exportFormat: TYPE_FORMATS[e.target.value]?.[0] || 'csv' }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {INTEGRATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Format export</label>
                <select value={form.exportFormat} onChange={e => setForm(f => ({ ...f, exportFormat: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {(TYPE_FORMATS[form.type]||['csv']).map(fmt => (
                    <option key={fmt} value={fmt}>{fmt.toUpperCase()}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                  className="w-4 h-4 rounded text-blue-600" />
                <span className="text-sm text-gray-700">Intégration active</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setShowAdd(false); setEditInteg(null) }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                Annuler
              </button>
              <button onClick={saveInteg} disabled={!form.name}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg transition-colors">
                {editInteg ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Run detail ──────────────────────────────────────────── */}
      {runDetail && (() => {
        let errors: string[] = []
        try { errors = JSON.parse(runDetail.errorDetails||'[]') } catch {}
        const sc = STATUS_CFG[runDetail.status] || STATUS_CFG.error
        return (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) setRunDetail(null) }}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4">
              <div className="flex items-start justify-between">
                <h2 className="text-lg font-bold text-gray-900">Détail du run</h2>
                <button onClick={() => setRunDetail(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-gray-500 text-xs">Intégration</p><p className="font-medium">{runDetail.integration?.name||'—'}</p></div>
                <div><p className="text-gray-500 text-xs">Statut</p>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${sc.cls}`}>{sc.icon} {sc.label}</span>
                </div>
                <div><p className="text-gray-500 text-xs">Période</p><p className="font-mono">{runDetail.period||'—'}</p></div>
                <div><p className="text-gray-500 text-xs">Enregistrements</p><p className="font-medium">{runDetail.recordCount}</p></div>
                <div><p className="text-gray-500 text-xs">Démarré</p><p>{format(new Date(runDetail.startedAt), "d MMM yyyy HH:mm:ss", { locale: fr })}</p></div>
                <div><p className="text-gray-500 text-xs">Terminé</p><p>{runDetail.finishedAt ? format(new Date(runDetail.finishedAt), "d MMM yyyy HH:mm:ss", { locale: fr }) : '—'}</p></div>
              </div>
              {runDetail.fileName && (
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-500">Fichier généré</p>
                  <p className="font-mono text-sm text-gray-700">{runDetail.fileName}</p>
                </div>
              )}
              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-red-700 mb-1">Erreurs ({errors.length})</p>
                  {errors.map((e, i) => <p key={i} className="text-xs text-red-600">• {e}</p>)}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                {runDetail.status === 'success' && (
                  <button onClick={() => { downloadRun(runDetail); setRunDetail(null) }}
                    className="flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors">
                    <Download size={14} /> Télécharger
                  </button>
                )}
                <button onClick={() => { replay(runDetail); setRunDetail(null) }}
                  className="flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg transition-colors">
                  <RotateCcw size={14} /> Rejouer
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
